import React from 'react'
import { Button, Input, InfoBox, CustomerServiceTelephone } from '@mobi/component-library/Common/V2'
import { DropdownInput } from '@mobi/component-library/Selection&Input'
import { DepositLimitFrequency } from '@mobi/api-types'
import { Spinner } from '@mobi/component-library/Common/Spinner'
import { Content, Footer, SignUpPageStyled } from './SignUpPage.styled'
import { AccountHeader } from '@mobi/component-library/Account'
import { getSignUpState } from '@mobi/account/Areas/SignUp/Store'
import {
  validateDepositLimitAmount,
  DEPOSIT_LIMIT_MIN,
  DEPOSIT_LIMIT_MAX,
  NUMERIC_REGEX,
} from '@mobi/account/Areas/SignUp/Utils/FormValidation'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { put, queryKeys, SignUpUrl } from '@mobi/account/Utils'
import { useSelector } from 'react-redux'
import { trackSignUpDepositLimits } from '../../Verification/Utils/ga'

const depositLimitFrequencies: Exclude<DepositLimitFrequency, null>[] = [
  'Daily',
  'Weekly',
  'Fortnightly',
  'Monthly',
]

type TypeDepositLimitAndAnniversaryRequest = {
  optInIndicator: boolean
  optInMarketingIndicator: boolean
  preCommitmentMaxDepositAmount?: number
  preCommitmentMaxDepositFrequency?: string
}

type PropDepositLimit = {
  newlyCreatedAccountNumber: number
  loggedInFirstName: string
  onCompleted: () => void
}

export const DepositLimit = ({
  newlyCreatedAccountNumber,
  loggedInFirstName,
  onCompleted,
}: PropDepositLimit) => {
  const [amount, setAmount] = React.useState('')
  const [frequency, setFrequency] = React.useState<DepositLimitFrequency>(null)
  const [amountValidationMessage, setAmountValidationMessage] = React.useState('')
  const signUpState = useSelector(getSignUpState)
  const queryClient = useQueryClient()

  const isAmountEditingAllowed = !!frequency
  const amountNumber = Number(amount)

  const saveDepositLimit = useMutation({
    mutationFn: async (isSettingDepositLimit: boolean) => {
      const shouldSetDepositLimit = isAmountEditingAllowed && isSettingDepositLimit

      const requestBody: TypeDepositLimitAndAnniversaryRequest = {
        optInIndicator: shouldSetDepositLimit,
        optInMarketingIndicator: signUpState?.signUp?.MarketingOptIn ?? false,
      }

      if (isAmountEditingAllowed) {
        requestBody.preCommitmentMaxDepositAmount = amountNumber
        requestBody.preCommitmentMaxDepositFrequency = frequency
      }

      return await put<TypeDepositLimitAndAnniversaryRequest, void>(
        SignUpUrl.saveDepositLimitAndAnniversary(newlyCreatedAccountNumber),
        requestBody
      )
    },
    mutationKey: [queryKeys.signupPrecommitment],
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: queryKeys.depositInitialData })
      document.getElementById('deposit-limit-anniversary-popup')?.remove()
      onCompleted()
    },
  })

  const onSubmit = () => {
    trackSignUpDepositLimits(frequency, amount, false)
    saveDepositLimit.mutate(true)
  }

  const onSkip = () => {
    trackSignUpDepositLimits(frequency, amount, true)
    saveDepositLimit.mutate(false)
  }

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    const sanitizedInputValue = inputValue.replace(NUMERIC_REGEX, '')
    setAmount(sanitizedInputValue)
    setAmountValidationMessage(validateDepositLimitAmount(sanitizedInputValue))
  }

  const applicationId = signUpState?.internetProspect?.ApplicationReferenceNumber?.toString()

  const ErrorInfoBox = () => {
    return (
      <InfoBox color='red' size='md'>
        <b>Sorry, an error occurred!</b>
        <div>
          There was an error in setting your deposit limit. Please try again, or contact us on{' '}
          <CustomerServiceTelephone />{' '}
          {!!applicationId && 'and provide the following Application ID '}
          so we can assist you in completing your registration.
          <br />
          <br />
          {!!applicationId && <span>Application ID: {applicationId}</span>}
        </div>
      </InfoBox>
    )
  }

  return (
    <SignUpPageStyled>
      {saveDepositLimit.isPending && <Spinner />}
      {saveDepositLimit.isError && <ErrorInfoBox />}
      <AccountHeader
        alignItems='start'
        title={`You're almost finished, ${loggedInFirstName}`}
        description='Would you like to set a deposit limit? Your deposit limit can also be set later.'
        style={{ marginBottom: '0rem' }}
      />
      <Content>
        <DropdownInput
          label='Select deposit frequency'
          name='sign-up-deposit-frequency'
          value={frequency || undefined}
          onChange={event => {
            setFrequency((event.target.value || null) as DepositLimitFrequency)
          }}
        >
          <option value=''>No Personal Limit</option>
          {depositLimitFrequencies.map(freq => (
            <option key={freq} value={freq}>
              {freq}
            </option>
          ))}
        </DropdownInput>
        <Input
          label='Enter limit amount'
          name='sign-up-deposit-limit-amount'
          helpText={
            amountValidationMessage
              ? amountValidationMessage
              : 'Any future increases to your deposit limit will include a 7 day cooling off period.'
          }
          prefix={amount.length > 0 && <div style={{ position: 'absolute' }}>$</div>}
          type='text'
          min={DEPOSIT_LIMIT_MIN}
          max={DEPOSIT_LIMIT_MAX}
          maxLength={DEPOSIT_LIMIT_MAX.toFixed(0).length}
          value={isAmountEditingAllowed ? amount : ''}
          onChange={handleAmountChange}
          isInvalid={isAmountEditingAllowed && !!amountValidationMessage}
          isDisabled={!isAmountEditingAllowed}
          placeholder={isAmountEditingAllowed ? 'Please enter an amount' : 'N/A'}
          inputMode='numeric'
        />
      </Content>
      <Footer>
        <Button
          color='primary'
          isFullWidth
          size='md'
          disabled={
            !!frequency &&
            (isNaN(amountNumber) ||
              amountNumber < DEPOSIT_LIMIT_MIN ||
              amountNumber > DEPOSIT_LIMIT_MAX ||
              !Number.isInteger(amountNumber))
          }
          onClick={onSubmit}
        >
          Set deposit limit
        </Button>
        <Button color='tertiary_color' isFullWidth size='md' onClick={onSkip}>
          Skip for now
        </Button>
      </Footer>
    </SignUpPageStyled>
  )
}
